import { put, fork, take, takeLatest } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { firestoreRef } from '../utils/firebase';
import * as actions from '../actions/pages.actions';
import { getEmptyTemplateConf } from './templates.saga';
import { isEmpty, unset, get } from 'lodash';
import { authenticationUtils, logUtils, pageUtils } from '../utils/common';
import { publishConfig } from '../config/env';

const pagesCollection = firestoreRef
    .collection('content')
    .doc('en')
    .collection('pages_tree');

function generateRouteKey(title) {
    title = title
        .toLowerCase()
        .replace(/[^a-z0-9 ]/g, '')
        .trim(); // only allow alphabet and numeric chars
    title = title.replace(/\s+/g, '-'); // remove spaces and replace with dash

    return title;
}

function* startListener() {
    const channel = new eventChannel(emitter => {
        const listener = pagesCollection.onSnapshot(snapshot => {
            const docs = [];
            snapshot.forEach(doc => {
                const document = {
                    id: doc.id,
                    ...doc.data()
                };
                docs.push(document);
            });
            emitter({ data: docs || [] });
        });

        return () => {
            listener.off();
        };
    });

    while (true) {
        const { data } = yield take(channel);
        yield put(actions.fetchPages(data));
    }
}

function* startIndexListener() {
    const channel = new eventChannel(emitter => {
        const listener = firestoreRef
            .collection('content')
            .doc('en')
            .onSnapshot(snapshot => {
                emitter({ data: snapshot.data() || {} });
            });

        return () => {
            listener.off();
        };
    });

    while (true) {
        const { data } = yield take(channel);
        yield put(actions.fetchIndex(data));
    }
}

function* createPageSaga(action) {
    yield put(actions.isCreating());

    let page = getPageObject(action.payload);
    page.statusProps = {
        lastSaved: {
            user: authenticationUtils.getUserEmail(),
            utcTimeStamp: new Date().toUTCString()
        }
    };

    if (page.path.split('/').length === 2) {
        yield createRootPage(page);
    } else {
        yield createChildPage(page);
    }

    yield put(actions.clearPageCreationState());
}

function getPageObject(page) {
    page.path = page.path + '/' + generateRouteKey(page.title);

    page.templateConf = {
        ...getEmptyTemplateConf(page.template)
    };

    page.showInMenu = false;

    page.subpages = {};

    return page;
}

function* createRootPage(page) {
    const pageKey = generateRouteKey(page.title);

    const canCreate = yield pagesCollection
        .doc(pageKey)
        .get()
        .then(doc => {
            if (!doc.exists) {
                return {
                    docExists: false
                };
            } else {
                return {
                    docExists: true,
                    msg: 'Page already exists.'
                };
            }
        })
        .catch(err => {
            return {
                docExists: true,
                msg: 'Firebase error.'
            };
        });

    if (!canCreate.docExists) {
        const success = yield pagesCollection
            .doc(pageKey)
            .set(page)
            .then(doc => {
                return true;
            })
            .catch(err => {
                return false;
            });

        if (success) {
            logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.CREATE_PAGE, { title: page.title, path: pageKey });
            yield put(actions.createPageSuccess());
        } else {
            yield put(actions.createPageError('Firebase error.'));
        }
    } else {
        yield put(actions.createPageError(canCreate.msg));
    }
}

function* createChildPage(page) {
    const rootKey = page.path.split('/')[1];

    // get root page
    const rootPage = yield pagesCollection
        .doc(rootKey)
        .get()
        .then(rootPage => {
            return rootPage;
        });

    yield checkRootForParent(rootPage, page);
}

function* checkRootForParent(rootPage, page) {
    let rootPageData = rootPage.data();
    const pageId = generateRouteKey(page.title);

    // get list of paths to navigate
    let pathChunks = page.path.split('/');
    pathChunks.splice(0, 2); // already remove empty and root page path
    pathChunks.pop(); // remove path of the page to add

    let curSubpages = rootPageData.subpages;
    while (pathChunks.length > 0) {
        curSubpages = curSubpages[pathChunks.shift()].subpages;
    }

    if (typeof curSubpages[pageId] === 'undefined') {
        curSubpages[pageId] = page;

        const success = yield pagesCollection
            .doc(rootPage.id)
            .set(rootPageData)
            .then(doc => {
                return true;
            })
            .catch(err => {
                return false;
            });

        if (success) {
            logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.CREATE_PAGE, { title: page.title, path: page.path });
            yield put(actions.createPageSuccess());
        } else {
            yield put(actions.createPageError('Firebase error.'));
        }
    } else {
        yield put(actions.createPageError('Page already exists.'));
    }
}

function* deletePageSaga(action) {
    yield put(actions.isDeleting());

    const { path } = action.payload;
    const rootPath = pageUtils.getRootFormPath(path, true);
    let publishedPageLocation = undefined;

    const authorSuccess = yield firestoreRef
        .runTransaction(transaction => {
            return transaction.get(pagesCollection.doc(rootPath)).then(rootPage => {
                let rootData = rootPage.data();
                if (pageUtils.isRootPagePath(path)) {
                    //For root pages, Delete the document

                    //Store the publish location.
                    if (rootData.hasOwnProperty('statusProps') && rootData.statusProps.hasOwnProperty('publishLocation')) {
                        publishedPageLocation = rootData.statusProps.publishLocation;
                    }

                    //Do the deletion
                    transaction.delete(pagesCollection.doc(rootPath));
                } else {
                    //For non-root pages, Update the document
                    const objPath = pageUtils.getObjectStructureFromPath(path);

                    //Store the publish location.
                    const savedPageObj = get(rootData, objPath);
                    if (savedPageObj && savedPageObj.hasOwnProperty('statusProps') && savedPageObj.statusProps.hasOwnProperty('publishLocation')) {
                        publishedPageLocation = savedPageObj.statusProps.publishLocation;
                    }
                    //Do the deletion
                    unset(rootData, objPath);
                    transaction.update(pagesCollection.doc(rootPath), rootData);
                }
            });
        })
        .then(() => true)
        .catch(() => false);

    if (authorSuccess) {
        logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.DELETE_PAGE, action.payload);

        if (publishedPageLocation !== undefined) {
            const deleteOnPublishSuccess = yield deletePageOnPublish(publishedPageLocation);
            if (deleteOnPublishSuccess === true) {
                logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.DELETE_PAGE_ON_PUBLISH, publishedPageLocation);
                yield put(actions.deletePageSuccess());
            } else {
                yield put(actions.deletePageError('Delete unsuccessful, something went wrong when deleteing on the publish environment.'));
            }
        } else {
            yield put(actions.deletePageSuccess());
        }
    } else {
        yield put(actions.deletePageError('Delete unsuccessful.'));
    }

    yield put(actions.clearPageDeletionState());
}
const deletePageOnPublish = async pathToDelete => {
    const token = await authenticationUtils.getUserToken().then(token => {
        return token;
    });
    return await fetch(publishConfig.deleteUrl, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + token,
            'access-control-allow-origin': '*',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ path: pathToDelete })
    })
        .then(res => {
            if (res.status === 200) {
                return true;
            } else {
                return false;
            }
        })
        .catch(() => {
            return false;
        });
};

export function* updatePagesSaga(action) {
    yield put(actions.isUpdating());
    let pages = action.payload;
    let success = false;

    if (isEmpty(pages)) {
        yield put(actions.updatePagesSuccess());
        success = true;
    } else {
        let batch = firestoreRef.batch();
        for (let docKey in pages) {
            let pageDoc = pagesCollection.doc(docKey);
            batch.set(pageDoc, pages[docKey], { merge: true });
        }
        success = yield batch
            .commit()
            .then(() => true)
            .catch(err => false);

        if (success) {
            yield put(actions.updatePagesSuccess());
        } else {
            yield put(actions.updatePagesError('Update unsuccessful.'));
        }
    }

    yield put(actions.clearPageUpdateState());
    return success;
}

export function* setIndexPageSaga(action) {
    const result = yield firestoreRef
        .collection('content')
        .doc('en')
        .update(action.payload)
        .then(doc => {
            return true;
        })
        .catch(err => {
            return false;
        });

    if (result) {
        logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.INDEX_UPDATED, action.payload);
        yield put(actions.setIndexSuccess());
    } else {
        yield put(actions.setIndexError('Update unsuccessful.'));
    }
}

export function* publishIndexPageSaga(action) {
    if (action.payload.index !== action.payload.savedIndex) {
        const saveResult = yield firestoreRef
            .collection('content')
            .doc('en')
            .update({ index: action.payload.index })
            .then(doc => {
                return true;
            })
            .catch(err => {
                return false;
            });

        if (!saveResult) {
            yield put(actions.setIndexError('Update unsuccessful.'));
            return;
        } else {
            logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.INDEX_UPDATED, action.payload);
        }
    }

    const token = yield authenticationUtils.getUserToken().then(token => {
        return token;
    });
    const result = yield fetch(publishConfig.indexUrl, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + token,
            'access-control-allow-origin': '*',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ index: action.payload.index })
    })
        .then(res => {
            if (res.status === 200) {
                return true;
            } else {
                return false;
            }
        })
        .catch(() => {
            return false;
        });

    if (result) {
        logUtils.logAuthorAction(logUtils.AUTHOR_ACTIONS.INDEX_PUBLISHED, action.payload);
        yield put(actions.setIndexSuccess());
    } else {
        yield put(actions.setIndexError('Update unsuccessful.'));
    }
}
export function* publishSiteNavSaga(action) {
    const token = yield authenticationUtils.getUserToken().then(token => {
        return token;
    });

    const result = yield fetch(publishConfig.siteNavigationURL, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + token,
            'access-control-allow-origin': '*',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(action.payload)
    })
        .then(res => {
            if (res.status === 200) {
                return true;
            } else {
                return false;
            }
        })
        .catch(() => {
            return false;
        });

    if (result) {
        yield put(actions.publishSiteNavigationSuccess());
    } else {
        yield put(actions.publishSiteNavigationError('Publish unsuccessful.'));
    }

    yield put(actions.clearPublishSiteNavigation());
}

export function* pagesSaga() {
    yield fork(startListener);
    yield fork(startIndexListener);

    yield takeLatest(actions.PagesActions.CREATE_PAGE, createPageSaga);
    yield takeLatest(actions.PagesActions.DELETE_PAGE, deletePageSaga);
    yield takeLatest(actions.PagesActions.UPDATE_PAGES, updatePagesSaga);
    yield takeLatest(actions.PagesActions.SET_INDEX_PAGE, setIndexPageSaga);
    yield takeLatest(actions.PagesActions.PUBLISH_INDEX_PAGE, publishIndexPageSaga);
    yield takeLatest(actions.PagesActions.PUBLISH_SITE_NAV, publishSiteNavSaga);
}
